import React from 'react';
import { Loader2, Truck, PackageCheck, ClipboardList } from 'lucide-react';

const GoodsReceiptDocument = ({
    data,
    isLoading = false,
    currency = 'NGN', // Added default currency fallback
    formatDate = (date) => new Date(date).toLocaleDateString(),
    formatCurrency = (amount, curr) =>
        new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: curr || 'NGN',
            minimumFractionDigits: 2
        }).format(amount)
}) => {

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="animate-spin text-emerald-600 mb-4" size={40} />
                <p className="text-slate-500 font-medium tracking-wide">Loading Reception Data...</p>
            </div>
        );
    }

    if (!data) return null;

    // Calculate total value based on quantity received and cost
    const totalShipmentValue = data.items_received?.reduce((acc, item) =>
        acc + (item.cost || 0 * item.quantity_received), 0
    );

    return (
        <div className="flex flex-col">
            {/* 1. Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-6 pb-6 border-b border-slate-100">
                <div>
                    <h2 className="text-xs font-black text-emerald-600 uppercase tracking-[0.2em] mb-1 text-emerald-600">Inventory Reception</h2>
                    <h1 className="text-xl font-bold text-slate-900 leading-tight">
                        {data.vendorname}
                    </h1>
                    <div className="flex items-center gap-2 mt-2 text-slate-500">
                        <Truck size={14} />
                        <span className="text-sm font-medium">Delivered to: {data.storename}</span>
                    </div>
                </div>
                <div className="text-right flex flex-col items-end">
                    <div className="bg-emerald-600 text-white px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest mb-2 flex items-center gap-2">
                        <PackageCheck size={12} /> Goods Received
                    </div>
                    <p className="text-xs font-bold text-slate-700">
                        {data.items_received?.[0] ? formatDate(data.items_received[0].received_on) : 'N/A'}
                    </p>
                </div>
            </div>

            {/* 2. Key Metrics Bar */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 py-6">
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Items Inbound</p>
                    <p className="text-sm font-bold text-slate-800">{data.items_received?.length} Lines</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Currency</p>
                    <p className="text-sm font-bold text-slate-800">{currency}</p>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                    <p className="text-[10px] font-bold text-slate-400 uppercase">Total Value (Cost)</p>
                    <p className="text-sm font-bold text-slate-800">{formatCurrency(totalShipmentValue, currency)}</p>
                </div>
            </div>

            {/* 3. Items Table */}
            <div className="border rounded-xl overflow-hidden border-slate-200 shadow-sm">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50 text-[10px] uppercase font-black text-slate-500 tracking-wider">
                            <th className="px-4 py-3">Product Description</th>
                            <th className="px-4 py-3 text-center">Ordered</th>
                            <th className="px-4 py-3 text-center">Received</th>
                            <th className="px-4 py-3 text-right">Unit Cost</th>
                            <th className="px-4 py-3 text-right">Ext. Cost</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                        {data.items_received?.map((item, idx) => {
                            const isPartial = item.quantity_received < item.quantity_ordered;
                            return (
                                <tr key={idx} className="text-sm hover:bg-slate-50/50 transition-colors">
                                    <td className="px-4 py-4">
                                        <p className="font-bold text-slate-800">{item.productname}</p>
                                        <p className="text-[10px] font-mono text-slate-400">PO Line #{item.podetail_id}</p>
                                    </td>
                                    <td className="px-4 py-4 text-center font-medium text-slate-500">{item.quantity_ordered}</td>
                                    <td className="px-4 py-4 text-center">
                                        <span className={`px-2 py-1 rounded text-xs font-bold ${isPartial ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                                            {item.quantity_received}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-right font-mono text-slate-600">
                                        {item.cost > 0 ? formatCurrency(item.cost, currency) : "-"}
                                    </td>
                                    <td className="px-4 py-4 text-right font-mono font-bold text-slate-900">
                                        {formatCurrency((item.cost || 0) * item.quantity_received, currency)}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* 4. Financial Summary */}
            <div className="mt-6 flex flex-col md:flex-row md:justify-end">
                <div className="w-full md:w-72 p-4 bg-emerald-50 rounded-xl border border-emerald-100">
                    <div className="flex justify-between items-center">
                        <span className="text-[10px] font-black uppercase text-emerald-800 tracking-widest">Total Inventory Value</span>
                    </div>
                    <p className="text-xl font-mono font-black text-emerald-700 mt-1">
                        {formatCurrency(totalShipmentValue, currency)}
                    </p>
                </div>
            </div>

            {/* 5. Document Footer */}
            <div className="mt-10 pt-6 border-t border-dashed border-slate-200 text-center">
                <p className="text-[10px] text-slate-400 font-medium uppercase tracking-widest">
                    Warehouse Receipt • {data.storename} • {currency} Operations
                </p>
            </div>
        </div>
    );
};

export default GoodsReceiptDocument;